import { useState, useRef, useEffect, useCallback } from 'react';
import { EmailComposeData, EmailComposeLink, Email } from '@/types/email';
import { buildReplyQuotedBody, buildForwardedBody } from '@/utils/emailQuotedMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  X,
  Save,
  Minimize2,
  Maximize2,
  Mail,
  Reply,
  Forward,
  Check,
  ChevronDown,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useEmailAccountSignature } from '@/hooks/email/useEmailAccountSignature';
import { useEmailRecipientLinks } from '@/hooks/email/useEmailRecipientLinks';
import { EmailChipInput } from './EmailChipInput';
import { EmailEditor, type TemplateContext } from './EmailEditor';
import { useAiCompose } from '@/hooks/useAiCompose';
import { ScheduleSendButton } from './ScheduleSendButton';
import { EmailLinkSuggestion } from './EmailLinkSuggestion';
import { EmailLinkSearchField } from './EmailLinkSearchField';

// --- Input style overrides (local to this modal, not global) ---
const inputClassName =
  'bg-white border-gray-300 focus-visible:ring-primary/20 focus-visible:border-primary transition-all duration-150';
const inputErrorClassName =
  'border-destructive focus-visible:ring-destructive/20 focus-visible:border-destructive';

function ProviderIcon({ provider }: { provider?: string }) {
  if (provider === 'gmail') {
    return (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
        <path d="M22 6.5V17.5C22 18.88 20.88 20 19.5 20H16V10.25L12 13.25L8 10.25V20H4.5C3.12 20 2 18.88 2 17.5V6.5C2 4.77 3.39 3.38 5.13 3.5C5.71 3.54 6.28 3.75 6.75 4.13L8 5L12 8L16 5L17.25 4.13C17.72 3.75 18.29 3.54 18.87 3.5C20.61 3.38 22 4.77 22 6.5Z" fill="#EA4335"/>
      </svg>
    );
  }
  if (provider === 'outlook') {
    return (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" fill="#0078D4"/>
        <path d="M12 8C10.34 8 9 9.34 9 11C9 12.66 10.34 14 12 14C13.66 14 15 12.66 15 11C15 9.34 13.66 8 12 8Z" fill="white"/>
      </svg>
    );
  }
  return <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

type SendState = 'idle' | 'sending' | 'sent' | 'error';

interface EmailAccountOption {
  id: string;
  email: string;
  provider: 'gmail' | 'outlook' | 'imap';
  user_id?: string;
  is_private?: boolean;
}

interface ComposeEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  replyTo?: Email;
  forwardEmail?: Email;
  onSend: (data: EmailComposeData) => void | Promise<{ success?: boolean } | void>;
  onSaveDraft: (data: EmailComposeData) => void;
  onScheduleSend?: (data: EmailComposeData, scheduledAt: Date) => Promise<{ success?: boolean } | void>;
  initialFormData?: { to: string[]; cc: string[]; bcc: string[]; subject: string; body: string };
  onFormDataChange?: (data: { to: string[]; cc: string[]; bcc: string[]; subject: string; body: string }) => void;
  /** Connected email accounts available for sending */
  accounts?: EmailAccountOption[];
  /** ID of the currently active account (pre-selected) */
  activeAccountId?: string;
  /** Deal/lead/user context for auto-resolving merge fields when a template is applied */
  templateContext?: TemplateContext;
  /** When true, opens with AI mode active and auto-generates a reply (used by "Responder com IA") */
  initialAiMode?: boolean;
}

export function ComposeEmailModal({
  open,
  onOpenChange,
  replyTo,
  forwardEmail,
  onSend,
  onSaveDraft,
  onScheduleSend,
  initialFormData,
  onFormDataChange,
  accounts = [],
  activeAccountId,
  templateContext,
  initialAiMode,
}: ComposeEmailModalProps) {
  const { toast } = useToast();

  const [isMinimized, setIsMinimized] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sendState, setSendState] = useState<SendState>('idle');
  const [subjectError, setSubjectError] = useState(false);
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [showSubjectConfirmation, setShowSubjectConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendStateTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const signatureInjectedForRef = useRef<string | null>(null);

  // --- AI Compose ---
  const {
    aiModeActive,
    isGenerating,
    promptText,
    quickPromptsReply,
    quickPromptsNew,
    toggleAiMode,
    setPromptText,
    generate,
    dismissAiMode,
  } = useAiCompose();

  // Initialize AI mode when opened via "Responder com IA"
  const hasInitAiRef = useRef(false);
  useEffect(() => {
    if (initialAiMode && open && !hasInitAiRef.current) {
      hasInitAiRef.current = true;
      toggleAiMode();
    }
    if (!open) {
      hasInitAiRef.current = false;
    }
  }, [open, initialAiMode]);

  // Insert AI-generated HTML (and optional subject), preserving signature block and quoted message
  const insertAiContent = useCallback((aiHtml: string, aiSubject?: string) => {
    setFormData((prev) => {
      const body = prev.body;
      const sigIdx = body.indexOf('<div data-signature=');
      const hrIdx = body.indexOf('<hr');
      // Find earliest separator (signature or quoted message divider)
      let cutPoint = -1;
      if (sigIdx !== -1 && hrIdx !== -1) cutPoint = Math.min(sigIdx, hrIdx);
      else if (sigIdx !== -1) cutPoint = sigIdx;
      else if (hrIdx !== -1) cutPoint = hrIdx;

      const newBody = cutPoint > 0 ? aiHtml + body.slice(cutPoint) : aiHtml;
      // Only fill subject if it's currently empty (don't overwrite user's input)
      const newSubject = aiSubject && !prev.subject.trim() ? aiSubject : prev.subject;
      return { ...prev, body: newBody, subject: newSubject };
    });
  }, []);

  // Auto-generate reply when opened via "Responder com IA" (fires once)
  const hasAutoGenRef = useRef(false);
  useEffect(() => {
    if (!initialAiMode || !open || !replyTo || hasAutoGenRef.current || isGenerating) return;
    hasAutoGenRef.current = true;
    const t = setTimeout(() => {
      generate({
        prompt: 'Responda a este email de forma profissional e cordial, abordando os pontos principais.',
        contextHtml: formData.body,
        onSuccess: insertAiContent,
      });
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialAiMode, replyTo, aiModeActive]);

  // Reset auto-gen flag when modal closes
  useEffect(() => {
    if (!open) {
      hasAutoGenRef.current = false;
    }
  }, [open]);

  // --- Form state ---
  const [formData, setFormData] = useState<EmailComposeData>({
    to: replyTo ? [replyTo.from.email] : (initialFormData?.to || []),
    cc: initialFormData?.cc || [],
    bcc: initialFormData?.bcc || [],
    subject: replyTo ? `Re: ${replyTo.subject}` : forwardEmail ? `Fwd: ${forwardEmail.subject}` : (initialFormData?.subject || ''),
    body: replyTo || forwardEmail ? getInitialBody() : (initialFormData?.body || ''),
    attachments: [],
    account_id: activeAccountId,
    replyToMessageId: replyTo?.id || undefined,
  });

  // --- Sync external initialFormData ---
  const hasInitializedFormDataRef = useRef(false);
  const lastInitialFormDataRef = useRef<string>('');

  useEffect(() => {
    if (!replyTo && !forwardEmail && initialFormData) {
      const serialized = JSON.stringify({
        to: initialFormData.to || [],
        cc: initialFormData.cc || [],
        bcc: initialFormData.bcc || [],
        subject: initialFormData.subject || '',
        body: initialFormData.body || ''
      });

      if (!hasInitializedFormDataRef.current || lastInitialFormDataRef.current !== serialized) {
        // Use initialFormData values directly — empty means intentionally cleared
        setFormData(prev => ({
          ...prev,
          to: initialFormData.to || [],
          cc: initialFormData.cc || [],
          bcc: initialFormData.bcc || [],
          subject: initialFormData.subject ?? prev.subject,
          body: initialFormData.body !== undefined ? initialFormData.body : prev.body,
        }));

        hasInitializedFormDataRef.current = true;
        lastInitialFormDataRef.current = serialized;
      }
    } else if (!hasInitializedFormDataRef.current) {
      hasInitializedFormDataRef.current = true;
    }
  }, [initialFormData?.to, initialFormData?.cc, initialFormData?.bcc, initialFormData?.subject, initialFormData?.body, replyTo, forwardEmail]);

  // --- Reset form when modal opens for new compose (not reply/forward) ---
  const prevOpenRef = useRef(open);
  const prevReplyToRef = useRef(replyTo);
  const prevForwardEmailRef = useRef(forwardEmail);

  useEffect(() => {
    const wasClosedNowOpen = !prevOpenRef.current && open;
    const wasReplyOrForward = prevReplyToRef.current || prevForwardEmailRef.current;
    const isNowNewEmail = !replyTo && !forwardEmail;

    // Reset when opening for new compose OR transitioning from reply/forward to new
    if ((wasClosedNowOpen && isNowNewEmail) || (wasReplyOrForward && isNowNewEmail)) {
      setFormData({
        to: initialFormData?.to || [],
        cc: initialFormData?.cc || [],
        bcc: initialFormData?.bcc || [],
        subject: initialFormData?.subject || '',
        body: initialFormData?.body || '',
        attachments: [],
        account_id: activeAccountId,
        replyToMessageId: undefined,
      });
      hasInitializedFormDataRef.current = false;
      lastInitialFormDataRef.current = '';
      setSubjectError(false);
      setSubjectTouched(false);
    }

    prevOpenRef.current = open;
    prevReplyToRef.current = replyTo;
    prevForwardEmailRef.current = forwardEmail;
  }, [replyTo, forwardEmail, open, initialFormData, activeAccountId]);

  // --- Sync sending account when modal opens or accounts load asynchronously ---
  const accountIdsKey = accounts.map((a) => a.id).join(',');

  useEffect(() => {
    if (!open || accounts.length === 0) return;

    const fallbackId = activeAccountId || accounts[0]?.id;
    if (!fallbackId) return;

    setFormData((prev) => {
      const valid =
        prev.account_id && accounts.some((a) => a.id === prev.account_id);
      if (valid) return prev;
      return { ...prev, account_id: fallbackId };
    });
  }, [open, activeAccountId, accountIdsKey]);

  useEffect(() => {
    if (!open || !replyTo) return;
    const toLine = replyTo.to?.length
      ? replyTo.to.map((t) => t.email).filter(Boolean).join(", ")
      : "—";
    const quoted = `<br/><br/><hr/><p><strong>Mensagem original</strong></p><p>De: ${replyTo.from.name} &lt;${replyTo.from.email}&gt;<br/>Para: ${toLine}<br/>Assunto: ${replyTo.subject}</p>${replyTo.body}`;
    const fallbackAccount = activeAccountId || accounts[0]?.id;
    setFormData({
      to: [replyTo.from.email].filter(Boolean),
      cc: [],
      bcc: [],
      subject: `Re: ${replyTo.subject}`,
      body: quoted,
      attachments: [],
      account_id: replyTo.accountId || fallbackAccount,
      replyToMessageId: replyTo.id || undefined,
    });
  }, [open, replyTo, activeAccountId, accountIdsKey]);

  // --- Persist form data (debounced, silent) ---
  const lastPersistedDataRef = useRef<string>('');

  useEffect(() => {
    if (onFormDataChange && !replyTo && !forwardEmail) {
      const serialized = JSON.stringify({
        to: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        subject: formData.subject,
        body: formData.body
      });

      if (lastPersistedDataRef.current !== serialized) {
        const id = setTimeout(() => {
          if (lastPersistedDataRef.current !== serialized) {
            onFormDataChange({
              to: formData.to,
              cc: formData.cc,
              bcc: formData.bcc,
              subject: formData.subject,
              body: formData.body
            });
            lastPersistedDataRef.current = serialized;
          }
        }, 500);
        return () => clearTimeout(id);
      }
    }
  }, [formData.to, formData.cc, formData.bcc, formData.subject, formData.body, onFormDataChange, replyTo, forwardEmail]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    if (!open || isMinimized) return;

    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key === 'Enter') {
        e.preventDefault();
        handleSend();
      } else if (mod && e.key === 's') {
        e.preventDefault();
        handleSaveDraft();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, isMinimized, formData]);

  // --- Cleanup send state timeout ---
  useEffect(() => {
    return () => {
      if (sendStateTimeoutRef.current) clearTimeout(sendStateTimeoutRef.current);
    };
  }, []);

  // --- Signature auto-insertion ---
  const { signatureHtml } = useEmailAccountSignature(open ? formData.account_id : undefined);

  useEffect(() => {
    if (!open || !signatureHtml) return;
    const accountId = formData.account_id;
    if (!accountId || signatureInjectedForRef.current === accountId) return;

    const sigBlock = `<div data-signature="true"><br/>--<br/>${signatureHtml}</div>`;

    // Adiar um tick: reset/sync do formulário no mesmo commit deixavam prev.body vazio e só a assinatura era aplicada
    const t = window.setTimeout(() => {
      if (signatureInjectedForRef.current === accountId) return;
      setFormData((prev) => {
        let body = prev.body;
        body = body.replace(/<div data-signature="true">[\s\S]*?<\/div>/, '');

        const hrIdx = body.indexOf('<hr/>');
        if (hrIdx !== -1) {
          body = body.slice(0, hrIdx) + sigBlock + body.slice(hrIdx);
        } else {
          body = body + sigBlock;
        }
        return { ...prev, body };
      });
      signatureInjectedForRef.current = accountId;
    }, 0);

    return () => clearTimeout(t);
  }, [open, signatureHtml, formData.account_id]);

  // Reset signature tracking when modal closes
  useEffect(() => {
    if (!open) {
      signatureInjectedForRef.current = null;
    }
  }, [open]);

  // --- Helpers ---
  function getInitialBody() {
    if (replyTo) return buildReplyQuotedBody(replyTo);
    if (forwardEmail) return buildForwardedBody(forwardEmail);
    return '';
  }

  // --- Auto-link suggestions based on recipients ---
  const { suggestions: linkSuggestions } = useEmailRecipientLinks(formData.to);

  const handleAcceptLink = useCallback((link: EmailComposeLink) => {
    setFormData((prev) => ({
      ...prev,
      links: [...(prev.links || []), link],
    }));
  }, []);

  const handleRemoveLink = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      links: (prev.links || []).filter((_, i) => i !== index),
    }));
  }, []);

  const handleFileAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

    const oversized = files.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      toast({
        title: "Arquivo muito grande",
        description: `"${oversized.name}" excede o limite de 10MB por arquivo.`,
        variant: "destructive",
      });
      if (event.target) event.target.value = '';
      return;
    }

    const currentTotal = (formData.attachments || []).reduce((sum, f) => sum + f.size, 0);
    const newTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + newTotal > MAX_TOTAL_SIZE) {
      toast({
        title: "Limite de anexos excedido",
        description: "O total de anexos não pode ultrapassar 25MB.",
        variant: "destructive",
      });
      if (event.target) event.target.value = '';
      return;
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...files]
    }));
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index) || []
    }));
  };

  const executeSend = useCallback(async () => {
    setSendState('sending');
    setShowSubjectConfirmation(false);

    try {
      const result = await Promise.resolve(onSend(formData));
      if ((result as { success?: boolean } | undefined)?.success !== false) {
        setSendState('sent');
        sendStateTimeoutRef.current = setTimeout(() => {
          setSendState('idle');
          onOpenChange(false);
        }, 1500);
      } else {
        setSendState('error');
        sendStateTimeoutRef.current = setTimeout(() => setSendState('idle'), 3000);
      }
    } catch {
      setSendState('error');
      sendStateTimeoutRef.current = setTimeout(() => setSendState('idle'), 3000);
    }
  }, [formData, onSend, onOpenChange]);

  const handleSend = useCallback(async () => {
    if (sendState === 'sending') return;

    if (!formData.to.length) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Adicione pelo menos um destinatário."
      });
      return;
    }

    if (!formData.subject.trim()) {
      setShowSubjectConfirmation(true);
      return;
    }

    executeSend();
  }, [formData, sendState, toast, executeSend]);

  const handleSaveDraft = useCallback(() => {
    onSaveDraft(formData);
    toast({
      title: "Rascunho salvo",
      description: "Seu email foi salvo como rascunho."
    });
  }, [formData, onSaveDraft, toast]);

  const handleScheduleSend = useCallback(async (scheduledAt: Date) => {
    if (!onScheduleSend) return;
    if (!formData.to.length) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Adicione pelo menos um destinatário."
      });
      return;
    }

    setSendState('sending');
    try {
      const result = await onScheduleSend(formData, scheduledAt);
      if ((result as { success?: boolean } | undefined)?.success !== false) {
        setSendState('sent');
        sendStateTimeoutRef.current = setTimeout(() => {
          setSendState('idle');
          onOpenChange(false);
        }, 1500);
      } else {
        setSendState('error');
        sendStateTimeoutRef.current = setTimeout(() => setSendState('idle'), 3000);
      }
    } catch {
      setSendState('error');
      sendStateTimeoutRef.current = setTimeout(() => setSendState('idle'), 3000);
    }
  }, [formData, onScheduleSend, onOpenChange, toast]);

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const hasContent = formData.to.length > 0 || formData.subject.trim() !== '' || formData.body.trim() !== '';

  // Accounts for the "From" selector — always show, even if empty (disabled)
  const availableAccounts = accounts.filter(a => a.email);
  const selectedAccount = availableAccounts.find(a => a.id === formData.account_id) || availableAccounts[0];

  // Title icon
  const TitleIcon = replyTo ? Reply : forwardEmail ? Forward : Mail;
  const titleText = replyTo ? 'Responder' : forwardEmail ? 'Encaminhar' : 'Novo Email';

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={(nextOpen) => {
        if (!nextOpen && hasContent && sendState === 'idle') {
          // Let user close — Radix handles it
        }
        onOpenChange(nextOpen);
      }}>
        <DialogContent
          hideClose
          className={cn(
            "max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0 rounded-2xl",
            isMinimized && "max-h-16"
          )}
        >
          {/* Header */}
          <DialogHeader className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-base font-bold text-foreground">
                <TitleIcon className="h-5 w-5 text-primary" />
                {titleText}
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="h-8 w-8"
                      aria-label={isMinimized ? 'Maximizar' : 'Minimizar'}
                    >
                      {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isMinimized ? 'Maximizar' : 'Minimizar'}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onOpenChange(false)}
                      className="h-8 w-8"
                      aria-label="Fechar"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Fechar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </DialogHeader>

          {!isMinimized && (
            <>
              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
                {/* From field — account selector (always visible) */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-muted-foreground">De</Label>
                  {availableAccounts.length === 0 ? (
                    <div className={cn(
                      'flex items-center gap-2 h-10 w-full rounded-lg border px-3 py-2 text-sm',
                      'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                    )}>
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Nenhuma conta conectada</span>
                    </div>
                  ) : availableAccounts.length === 1 ? (
                    <div className="flex items-center gap-2 h-10 w-full px-3 py-2 text-sm">
                      <ProviderIcon provider={selectedAccount?.provider} />
                      <span className="text-muted-foreground">{selectedAccount?.email}</span>
                    </div>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'flex items-center justify-between gap-2 h-10 w-full rounded-lg border px-3 py-2 text-sm cursor-pointer',
                            inputClassName,
                            'hover:border-gray-400'
                          )}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <ProviderIcon provider={selectedAccount?.provider} />
                            <span className="truncate text-foreground">
                              {selectedAccount?.email || 'Selecionar conta'}
                            </span>
                          </div>
                          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                        {availableAccounts.map((account) => (
                          <DropdownMenuItem
                            key={account.id}
                            onClick={() => setFormData(prev => ({ ...prev, account_id: account.id }))}
                            className={cn(
                              'flex items-center gap-2 cursor-pointer',
                              account.id === formData.account_id && 'bg-primary/5'
                            )}
                          >
                            <ProviderIcon provider={account.provider} />
                            <span className="truncate">{account.email}</span>
                            {account.id === formData.account_id && (
                              <Check className="h-4 w-4 text-primary ml-auto shrink-0" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* To field with CC/CCO buttons */}
                <div className="space-y-1.5">
                  <Label htmlFor="to" className="text-sm font-semibold text-muted-foreground">
                    Para
                  </Label>
                  <div className="flex items-start gap-2">
                    <EmailChipInput
                      id="to"
                      value={formData.to}
                      onChange={(emails) => setFormData(prev => ({ ...prev, to: emails }))}
                      placeholder="destinatario@email.com"
                      className="flex-1"
                      autoFocus
                    />
                    <div className="flex items-center gap-1 pt-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCc(!showCc)}
                        className={cn(
                          'text-sm font-medium px-2 py-1 h-auto rounded',
                          showCc
                            ? 'text-primary bg-primary/5'
                            : 'text-primary hover:bg-primary/5'
                        )}
                      >
                        CC
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBcc(!showBcc)}
                        className={cn(
                          'text-sm font-medium px-2 py-1 h-auto rounded',
                          showBcc
                            ? 'text-primary bg-primary/5'
                            : 'text-primary hover:bg-primary/5'
                        )}
                      >
                        CCO
                      </Button>
                    </div>
                  </div>

                  {/* Auto-link suggestions based on recipient emails */}
                  {linkSuggestions.length > 0 && (
                    <EmailLinkSuggestion
                      suggestions={linkSuggestions}
                      onAccept={handleAcceptLink}
                      onDismiss={() => {}}
                      acceptedLinks={formData.links || []}
                    />
                  )}
                </div>

                {/* CC field */}
                {showCc && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-1 duration-150">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="cc" className="text-sm font-semibold text-muted-foreground">CC</Label>
                      <button
                        type="button"
                        onClick={() => { setShowCc(false); setFormData(prev => ({ ...prev, cc: [] })); }}
                        className="text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors"
                        aria-label="Fechar campo CC"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <EmailChipInput
                      id="cc"
                      value={formData.cc || []}
                      onChange={(emails) => setFormData(prev => ({ ...prev, cc: emails }))}
                      placeholder="destinatario@email.com"
                    />
                  </div>
                )}

                {/* BCC field */}
                {showBcc && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-1 duration-150">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="bcc" className="text-sm font-semibold text-muted-foreground">CCO</Label>
                      <button
                        type="button"
                        onClick={() => { setShowBcc(false); setFormData(prev => ({ ...prev, bcc: [] })); }}
                        className="text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors"
                        aria-label="Fechar campo CCO"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <EmailChipInput
                      id="bcc"
                      value={formData.bcc || []}
                      onChange={(emails) => setFormData(prev => ({ ...prev, bcc: emails }))}
                      placeholder="destinatario@email.com"
                    />
                  </div>
                )}

                {/* Manual link search — "Vincular a deal ou lead" */}
                <EmailLinkSearchField
                  links={formData.links || []}
                  onAddLink={handleAcceptLink}
                  onRemoveLink={handleRemoveLink}
                />

                {/* AI Prompt Bar */}
                {aiModeActive && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2 animate-in slide-in-from-top-1 duration-150">
                    {/* Quick prompt chips */}
                    <div className="flex flex-wrap gap-1.5">
                      {(replyTo ? quickPromptsReply : quickPromptsNew).map((qp) => (
                        <button
                          key={qp.id}
                          type="button"
                          onClick={() => setPromptText(qp.prompt)}
                          className="px-2.5 py-0.5 text-xs rounded-full border border-primary/40 bg-white text-primary hover:bg-primary/10 transition-colors"
                        >
                          {qp.label}
                        </button>
                      ))}
                    </div>
                    {/* Prompt input + Generate button */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isGenerating && promptText.trim()) {
                            e.preventDefault();
                            generate({
                              prompt: promptText,
                              contextHtml: formData.body,
                              onSuccess: insertAiContent,
                            });
                          }
                        }}
                        placeholder={
                          replyTo
                            ? 'Descreva como responder... (ex: confirmar reunião quinta 15h)'
                            : 'Descreva o email a gerar... (ex: prospecção para empresa de logística)'
                        }
                        disabled={isGenerating}
                        className="flex-1 text-sm px-3 py-1.5 rounded-md border border-primary/30 bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:opacity-50"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={isGenerating || !promptText.trim()}
                        onClick={() =>
                          generate({
                            prompt: promptText,
                            contextHtml: formData.body,
                            onSuccess: insertAiContent,
                          })
                        }
                        className="gap-1.5 shrink-0"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Gerando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3.5 w-3.5" />
                            Gerar
                          </>
                        )}
                      </Button>
                    </div>
                    <button
                      type="button"
                      onClick={dismissAiMode}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Fechar modo IA
                    </button>
                  </div>
                )}

                {/* Subject */}
                <div className="space-y-1.5">
                  <Label htmlFor="subject" className="text-sm font-semibold text-muted-foreground">
                    Assunto <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="subject"
                    placeholder="Assunto do email"
                    value={formData.subject}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, subject: e.target.value }));
                      if (subjectError && e.target.value.trim()) setSubjectError(false);
                    }}
                    onBlur={() => {
                      setSubjectTouched(true);
                      if (!formData.subject.trim()) {
                        setSubjectError(true);
                      }
                    }}
                    className={cn(inputClassName, subjectError && inputErrorClassName)}
                    aria-required="true"
                    aria-invalid={subjectError}
                    aria-describedby={subjectError ? 'subject-error' : undefined}
                  />
                  {subjectError && (
                    <p id="subject-error" className="text-xs text-destructive mt-1">
                      Assunto é obrigatório
                    </p>
                  )}
                </div>

                {/* Attachments */}
                {formData.attachments && formData.attachments.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-muted-foreground">Anexos</Label>
                    <div className="flex flex-wrap gap-2">
                      {formData.attachments.map((file, index) => (
                        <Badge key={index} variant="secondary" className="gap-2 bg-gray-100 border border-gray-200">
                          <span className="truncate max-w-32 text-foreground">{file.name}</span>
                          <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive transition-colors"
                            aria-label={`Remover anexo ${file.name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message Body — Rich Text */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-muted-foreground">Mensagem</Label>
                  <EmailEditor
                    id="body"
                    initialContent={formData.body}
                    onChange={(html) => setFormData(prev => ({ ...prev, body: html }))}
                    placeholder="Digite sua mensagem aqui..."
                    minHeight={200}
                    maxHeight={300}
                    onAttach={() => fileInputRef.current?.click()}
                    attachmentCount={formData.attachments?.length || 0}
                    onAI={toggleAiMode}
                    aiModeActive={aiModeActive}
                    templateContext={templateContext}
                    onTemplateSelect={(bodyHtml, subject) => {
                      setFormData(prev => ({
                        ...prev,
                        body: bodyHtml,
                        subject: subject && !prev.subject.trim() ? subject : prev.subject,
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Subject confirmation banner */}
              {showSubjectConfirmation && (
                <div className="flex items-center justify-between px-5 py-2.5 bg-amber-50 border-t border-amber-200 text-sm text-amber-800">
                  <span>Enviar email sem assunto?</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSubjectConfirmation(false)}
                      className="h-7 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={executeSend}
                      className="h-7 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Enviar assim
                    </Button>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-3 border-t border-gray-200">
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileAttachment}
                  className="hidden"
                />
                <div className="flex items-center justify-between">
                  {/* Left: keyboard hint */}
                  <span className="text-xs text-muted-foreground hidden sm:block">
                    Ctrl + Enter para enviar
                  </span>

                  {/* Right: Draft + Send */}
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSaveDraft}
                          className="gap-2 border-gray-300 text-muted-foreground hover:text-foreground px-4 py-2.5"
                        >
                          <Save className="h-4 w-4" />
                          Rascunho
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Salvar rascunho (Ctrl+S)</TooltipContent>
                    </Tooltip>

                    <ScheduleSendButton
                      onSend={handleSend}
                      onSchedule={handleScheduleSend}
                      sendState={sendState}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
